import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";

const empty = {
    name: "", slug: "", category: "Bento Cakes",
    short_description: "", long_description: "",
    image_url: "", gallery: [],
    price: 0, price_tiers: [], stock: 100, options: [],
    order: 0, is_active: true,
};

const CATEGORIES = ["Bento Cakes", "Floral Bouquets", "Cake in a Jar", "Cupcakes", "Vintage Cakes"];

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [optionsText, setOptionsText] = useState("");

    const load = () => api.get("/products?active_only=false").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing("new");
        setForm(empty);
        setOptionsText("");
    };
    const openEdit = (p) => {
        setEditing(p.id);
        setForm({ ...p });
        setOptionsText((p.options || []).join(", "));
    };
    const close = () => { setEditing(null); setForm(empty); setOptionsText(""); };

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            price: parseFloat(form.price) || 0,
            stock: parseInt(form.stock) || 0,
            options: optionsText.split(",").map((s) => s.trim()).filter(Boolean),
            price_tiers: (form.price_tiers || [])
                .map((t) => ({ qty: parseInt(t.qty) || 0, price: parseFloat(t.price) || 0 }))
                .filter((t) => t.qty > 0 && t.price > 0),
        };
        try {
            if (editing === "new") await api.post("/products", payload);
            else await api.put(`/products/${editing}`, payload);
            toast.success("Saved"); close(); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (p) => {
        try {
            await api.put(`/products/${p.id}`, { ...p, is_active: !p.is_active });
            toast.success(p.is_active ? "Disabled — hidden from shop" : "Enabled — now in shop");
            load();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-products">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-3xl">Shop Products</h2>
                    <p className="text-sm text-ink-soft mt-1">Online-purchasable items — Bento, Cupcakes, Jars, Bouquets, Vintage.</p>
                </div>
                <button onClick={openNew} className="btn-pink" data-testid="add-product"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((p, i) => (
                    <div key={p.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`product-row-${i}`}>
                        <img src={p.image_url} alt={p.name} className="w-16 h-16 object-cover bg-blush-pale" />
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg">{p.name}</p>
                            <p className="text-xs text-ink-soft">{p.category} · /{p.slug} · ${Number(p.price).toFixed(2)} AUD</p>
                        </div>
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {p.is_active ? "Active" : "Hidden"}
                        </span>
                        <div className="flex gap-3 items-center ml-2">
                            <button onClick={() => toggleActive(p)} className={p.is_active ? "text-green-600 hover:text-blush" : "text-gray-400 hover:text-blush"} title={p.is_active ? "Disable (hide from shop)" : "Enable (show in shop)"} data-testid={`toggle-product-${i}`}>
                                {p.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button onClick={() => openEdit(p)} data-testid={`edit-product-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(p.id)} data-testid={`delete-product-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-center text-ink-soft py-12">No products yet — click "New" to add one.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Product</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Slug (e.g. bento-cake-pair)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                            </div>
                            <select className="w-full px-4 py-3 border border-[#EAE3DE] bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Short description" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={4} placeholder="Long description" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} required />
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Product Image" testIdPrefix="product-image" />
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Price (AUD)</label>
                                    <input type="number" step="0.01" min="0" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Stock</label>
                                    <input type="number" min="0" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Order</label>
                                    <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Options / Flavours (comma-separated)</label>
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="e.g. Vanilla, Chocolate, Red Velvet" value={optionsText} onChange={(e) => setOptionsText(e.target.value)} />
                            </div>
                            {/* Tiered / bulk pricing */}
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                                    Bulk Pricing Tiers (optional)
                                </label>
                                <p className="text-[11px] text-ink-soft mb-3">
                                    e.g. base price $15, add a tier <em>qty 4 → total $55</em>. Cart automatically picks the best price for the buyer.
                                </p>
                                <div className="space-y-2">
                                    {(form.price_tiers || []).map((t, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <span className="text-xs text-ink-soft w-14">Buy</span>
                                            <input
                                                type="number"
                                                min="2"
                                                placeholder="4"
                                                value={t.qty}
                                                onChange={(e) => {
                                                    const tiers = [...form.price_tiers];
                                                    tiers[idx] = { ...tiers[idx], qty: e.target.value };
                                                    setForm({ ...form, price_tiers: tiers });
                                                }}
                                                className="w-20 px-3 py-2 border border-[#EAE3DE]"
                                            />
                                            <span className="text-xs text-ink-soft">for total $</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="55"
                                                value={t.price}
                                                onChange={(e) => {
                                                    const tiers = [...form.price_tiers];
                                                    tiers[idx] = { ...tiers[idx], price: e.target.value };
                                                    setForm({ ...form, price_tiers: tiers });
                                                }}
                                                className="w-24 px-3 py-2 border border-[#EAE3DE]"
                                            />
                                            <span className="text-xs text-ink-soft">
                                                {t.qty > 0 && t.price > 0 && `(≈ $${(Number(t.price) / Number(t.qty)).toFixed(2)} each)`}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const tiers = form.price_tiers.filter((_, i) => i !== idx);
                                                    setForm({ ...form, price_tiers: tiers });
                                                }}
                                                className="ml-auto text-ink-soft hover:text-blush"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, price_tiers: [...(form.price_tiers || []), { qty: "", price: "" }] })}
                                    className="mt-3 text-xs tracking-[0.2em] uppercase text-blush hover:text-[#C4007A] inline-flex items-center gap-1"
                                >
                                    <Plus size={12} /> Add tier
                                </button>
                            </div>
                            <label className="flex items-center gap-2 pt-2">
                                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                <span className="text-sm">Active (visible in shop)</span>
                            </label>
                            <button type="submit" className="btn-pink w-full">Save Product</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
